import React, { useEffect, useState } from 'react';

import s from '../styles/EmailUnconfirmedModal.module.css';

function EmailUnconfirmedModal({ currentUser, onClose, onUserUpdated }) {
  const [resendPending, setResendPending] = useState(false);
  const [changePending, setChangePending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [suggestResendAfterChange, setSuggestResendAfterChange] = useState(false);

  const userEmail = currentUser?.login || '';

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }

    const timerId = window.setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [cooldown]);

  const buildAuthHeaders = () => {
    const token = localStorage.getItem('user_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const mapErrorText = (responseStatus, fallback) => {
    if (responseStatus === 429) return 'Слишком много запросов. Подождите немного и попробуйте снова.';
    if (responseStatus === 401) return 'Сессия истекла. Войдите снова, чтобы продолжить.';
    if (responseStatus === 403) return 'Для этого действия не хватает прав.';
    return fallback || 'Не удалось выполнить действие. Попробуйте позже.';
  };

  const handleResend = async () => {
    if (resendPending || cooldown > 0) return;

    setResendPending(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const response = await fetch('/api/email/resend-confirmation', {
        method: 'POST',
        headers: buildAuthHeaders(),
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setErrorMessage(mapErrorText(response.status, data.error));
        if (data.seconds_to_retry) {
          setCooldown(Number(data.seconds_to_retry) || 0);
        }
        return;
      }

      setStatusMessage(`Письмо отправлено на ${data.masked_email || userEmail}.`);
      setSuggestResendAfterChange(false);
      setCooldown(Number(data.seconds_to_retry) || 0);
    } catch (error) {
      setErrorMessage('Сетевая ошибка. Проверьте интернет и попробуйте снова.');
    } finally {
      setResendPending(false);
    }
  };

  const handleChangeEmail = async () => {
    if (changePending || resendPending) return;

    const normalized = newEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalized)) {
      setErrorMessage('Введите корректный email.');
      return;
    }

    setChangePending(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const response = await fetch('/api/email/change-unconfirmed', {
        method: 'POST',
        headers: buildAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ new_email: normalized }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setErrorMessage(mapErrorText(response.status, data.error));
        if (data.seconds_to_retry) {
          setCooldown(Number(data.seconds_to_retry) || 0);
        }
        return;
      }

      if (typeof onUserUpdated === 'function') {
        await onUserUpdated();
      }

      setStatusMessage(`Email изменен. Письмо отправлено на ${data.masked_email || normalized}.`);
      setCooldown(Number(data.seconds_to_retry) || 0);
      setShowChangeEmail(false);
      setSuggestResendAfterChange(true);
      setNewEmail('');
    } catch (error) {
      setErrorMessage('Сетевая ошибка. Не удалось сменить email.');
    } finally {
      setChangePending(false);
    }
  };

  return (
    <div className={s.overlay} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <button className={s.closeButton} onClick={onClose} aria-label="Закрыть">
          ×
        </button>

        <h3 className={s.title}>Подтвердите почту</h3>
        <p className={s.description}>
          Мы отправили письмо со ссылкой для подтверждения. Перейдите по ней, чтобы открыть все функции.
        </p>
        <p className={s.description}>Текущий адрес: <strong>{userEmail || 'не указан'}</strong></p>
        <p className={s.tipText}>
          Не получили письмо? Проверьте папку <strong>Спам</strong> или <strong>Промоакции</strong> — иногда письма попадают туда. Если адрес введён с ошибкой, смените его ниже.
        </p>

        <div className={s.actions}>
          <button
            type="button"
            className={s.primaryButton}
            onClick={handleResend}
            disabled={resendPending || cooldown > 0 || changePending}
          >
            {resendPending
              ? 'Отправляем...'
              : cooldown > 0
                ? `Повторно через ${cooldown}с`
                : 'Отправить письмо повторно'}
          </button>

          <button
            type="button"
            className={s.secondaryButton}
            onClick={() => {
              setShowChangeEmail((prev) => !prev);
              setErrorMessage('');
              setStatusMessage('');
            }}
            disabled={changePending || resendPending}
          >
            Сменить почту
          </button>
        </div>

        {showChangeEmail && (
          <div className={s.changeBlock}>
            <input
              type="email"
              className={s.input}
              placeholder="Новый email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={changePending || resendPending}
            />
            <button
              type="button"
              className={s.primaryButton}
              onClick={handleChangeEmail}
              disabled={changePending || resendPending}
            >
              {changePending ? 'Сохраняем...' : 'Подтвердить смену email'}
            </button>
          </div>
        )}

        {suggestResendAfterChange && cooldown === 0 && (
          <p className={s.tipText}>Если письмо не пришло, нажмите «Отправить письмо повторно».</p>
        )}

        {statusMessage && <p className={s.successText}>{statusMessage}</p>}
        {errorMessage && <p className={s.errorText}>{errorMessage}</p>}
      </div>
    </div>
  );
}

export default EmailUnconfirmedModal;
